import { useState } from "react";
import type { FieldIssue } from "./field-validation";
import { focusValidationIssue } from "./components/ValidationSummary";

export interface ValidationField {
  id: string;
  label: string;
  value: unknown;
  required?: boolean;
  requiredMessage?: string;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  patternMessage?: string;
  validate?: (value: unknown) => string | undefined;
}

export function validateFormFields(
  sectionLabel: string,
  fields: ValidationField[],
): FieldIssue[] {
  return fields.flatMap((field) => {
    const empty =
      field.value === undefined ||
      field.value === null ||
      (typeof field.value === "string" && !field.value.trim());
    let reason: string | undefined;
    let kind: FieldIssue["kind"] = "invalid";
    if (empty && field.required) {
      reason =
        field.requiredMessage ||
        `Enter ${field.label.toLocaleLowerCase("en")}. This field is required.`;
      kind = "required";
    } else if (!empty) {
      const text = String(field.value);
      if (field.minLength !== undefined && text.length < field.minLength)
        reason = `Enter at least ${field.minLength} characters.`;
      else if (field.maxLength !== undefined && text.length > field.maxLength)
        reason = `Use no more than ${field.maxLength} characters.`;
      else if (
        field.pattern &&
        !new RegExp(
          field.pattern.source,
          field.pattern.flags.replace(/[gy]/g, ""),
        ).test(text)
      )
        reason =
          field.patternMessage || "The value is not in the required format.";
    }
    if (!reason) reason = field.validate?.(field.value);
    return reason
      ? [
          {
            id: `${field.id}-issue`,
            targetId: field.id,
            path: [field.id],
            sectionId: sectionLabel,
            sectionLabel,
            fieldLabel: field.label,
            kind,
            reason,
          },
        ]
      : [];
  });
}

/** Controlled forms recalculate on every render; no blur or submit required. */
export function useFormValidation(
  sectionLabel: string,
  fields: ValidationField[],
) {
  const issues = validateFormFields(sectionLabel, fields);
  const issue = (id: string) => issues.find((item) => item.targetId === id);
  return {
    issues,
    issue,
    fieldProps: (id: string) => ({
      id,
      "aria-label": fields.find((field) => field.id === id)?.label,
      "aria-invalid": Boolean(issue(id)),
      "aria-describedby": issue(id) ? `${id}-error` : undefined,
    }),
    validate: () => {
      if (issues[0]) focusValidationIssue(issues[0]);
      return issues.length === 0;
    },
  };
}

interface FormInteraction {
  scopeKey: string;
  touched: ReadonlySet<string>;
  submitted: boolean;
}

/**
 * Opt-in validation for account forms: reveal a field after blur, or all current
 * errors after submit. Supply only the active fields; validation never mutates
 * their values. A mode-specific scope key resets interaction, not form data.
 */
export function useInteractiveFormValidation(
  sectionLabel: string,
  fields: ValidationField[],
  scopeKey = sectionLabel,
) {
  const emptyInteraction = (): FormInteraction => ({
    scopeKey,
    touched: new Set<string>(),
    submitted: false,
  });
  const [storedInteraction, setInteraction] =
    useState<FormInteraction>(emptyInteraction);
  let interaction = storedInteraction;
  if (storedInteraction.scopeKey !== scopeKey) {
    // Reset this component's state before React commits the new mode. An effect
    // would allow old validation to flash or reappear on an A -> B -> A switch.
    interaction = emptyInteraction();
    setInteraction(interaction);
  }
  const allIssues = validateFormFields(sectionLabel, fields);
  const issues = allIssues.filter(
    (item) => interaction.submitted || interaction.touched.has(item.targetId),
  );
  const issue = (id: string) => issues.find((item) => item.targetId === id);
  return {
    issues,
    issue,
    fieldProps: (id: string) => ({
      id,
      "aria-label": fields.find((field) => field.id === id)?.label,
      "aria-invalid": Boolean(issue(id)),
      "aria-describedby": issue(id) ? `${id}-error` : undefined,
      onBlur: () => {
        setInteraction((previous) =>
          previous.scopeKey !== scopeKey || previous.touched.has(id)
            ? previous
            : { ...previous, touched: new Set([...previous.touched, id]) },
        );
      },
    }),
    validate: () => {
      setInteraction((previous) =>
        previous.scopeKey !== scopeKey || previous.submitted
          ? previous
          : { ...previous, submitted: true },
      );
      if (allIssues[0]) focusValidationIssue(allIssues[0]);
      return allIssues.length === 0;
    },
    reset: () => {
      setInteraction((previous) =>
        previous.scopeKey === scopeKey ? emptyInteraction() : previous,
      );
    },
  };
}
